"use client";

import * as Dialog from "@radix-ui/react-dialog";
import Image from "next/image";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import CloseIcon from "../_components/icons/close-icon";

type AlertDialogContextValue = {
  open: boolean;
  showAlertDialog: (args: {
    title: string;
    mediumBody?: string | null;
    normalBody?: string | null;
  }) => void;
};

const AlertDialogContext = createContext<AlertDialogContextValue | undefined>(
  undefined,
);

function AlertDialog({
  title,
  mediumBody,
  normalBody,
  open,
  setOpen,
}: {
  title: string;
  mediumBody: string | null;
  normalBody: string | null;
  open: boolean;
  setOpen: (arg: boolean) => void;
}) {
  return (
    <div className="w-[315px] md:w-[690px]">
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="sd-overlay">
            <Dialog.Close className="sd-close">
              <CloseIcon />
            </Dialog.Close>
            <Dialog.Content className="bg-white p-[30px]">
              <Dialog.Title className="mb-[40px] flex h-[40px] items-center justify-center border border-[#FF5C38] bg-[#FFCFC0] text-[12px] font-medium text-[#FF5C38]">
                <Image
                  src={"/i/snackbar_icon_alert.svg"}
                  alt="success"
                  width={16}
                  height={16}
                  className="mr-[8px]"
                />
                {title}
              </Dialog.Title>
              <Dialog.Description asChild>
                <div>
                  {mediumBody && (
                    <div className="my-[20px] text-[14px] font-bold">
                      {mediumBody}
                    </div>
                  )}
                  {normalBody && (
                    <div className="my-[20px] text-[13px] font-medium whitespace-pre-wrap">
                      {normalBody}
                    </div>
                  )}
                </div>
              </Dialog.Description>
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

export const AlertDialogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState<string>("");
  const [mediumBody, setMediumBody] = useState<string | null>("");
  const [normalBody, setNormalBody] = useState<string | null>("");

  // Track mount status to avoid state updates before mount
  const isMountedRef = useRef(false);
  const pendingAlertRef = useRef<{
    title: string;
    mediumBody: string | null;
    normalBody: string | null;
  } | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    // Process any pending alert that was queued before mount
    if (pendingAlertRef.current) {
      const { title, mediumBody, normalBody } = pendingAlertRef.current;
      pendingAlertRef.current = null;
      setTitle(title);
      setMediumBody(mediumBody);
      setNormalBody(normalBody);
      setOpen(true);
    }

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const showAlertDialog = useCallback(
    ({
      title,
      mediumBody,
      normalBody,
    }: {
      title: string;
      mediumBody?: string | null;
      normalBody?: string | null;
    }) => {
      const alertData = {
        title,
        mediumBody: mediumBody ?? null,
        normalBody: normalBody ?? null,
      };

      // If not mounted yet, queue the alert for after mount
      if (!isMountedRef.current) {
        pendingAlertRef.current = alertData;
        return;
      }

      // Component is mounted, safe to update state
      setTitle(alertData.title);
      setMediumBody(alertData.mediumBody);
      setNormalBody(alertData.normalBody);
      setOpen(true);
    },
    [],
  );

  return (
    <AlertDialogContext.Provider value={{ open, showAlertDialog }}>
      {children}
      <AlertDialog
        title={title}
        mediumBody={mediumBody}
        normalBody={normalBody}
        open={open}
        setOpen={setOpen}
      />
    </AlertDialogContext.Provider>
  );
};

export function useAlertDialog(): AlertDialogContextValue["showAlertDialog"] {
  const context = useContext(AlertDialogContext);
  if (!context) {
    throw new Error("useAlertDialog must be used within a AlertDialogProvider");
  }
  return context.showAlertDialog;
}
